import Cropper from "cropperjs";
import "cropperjs/dist/cropper.css";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../front-end-global-components/components/Button/Button";
import InputBox from "../../front-end-global-components/components/InputBox/InputBox";
import Modal from "../../front-end-global-components/components/Modal/Modal";
import { uploadDocument } from "../../redux/documents/actions";
import { setErrorStatus } from "../../redux/status/actions";
import { isValidArray } from "../../utils/validators";
import {
  MAX_PDF_TOTAL_BYTES,
  MAX_UPLOAD_FILES,
  getPdfThumbnail
} from "../../utils/pdf";
import "./Home.css";

const newItemId = () => `${Date.now()}-${Math.random()}`;
const isImage = (file) =>
  file.type === "image/jpeg" || file.type === "image/png";
const isPdf = (file) => file.type === "application/pdf";

export default function UploadDocument(props) {
  const [cropperReady, setCropperReady] = useState(false);
  const cropper = useRef(null);
  // images picked together wait here and go through the cropper one by one
  const cropQueue = useRef([]);
  const croppingFile = useRef(null);
  // PDFs still being read for thumbnails; they count toward the batch limits
  const pendingPdfs = useRef([]);

  const navigate = useNavigate();

  function cropHandler() {
    if (!cropper.current) {
      return;
    }

    const croppedCanvas = cropper.current.getCroppedCanvas();
    if (!croppedCanvas) {
      return;
    }

    const name = croppingFile.current?.name || `${Date.now()}.png`;
    croppedCanvas.toBlob(
      (blob) => {
        if (!blob) {
          return;
        }

        const newFile = new File([blob], name, { type: "image/png" });
        props.setSrc((prev) => [
          ...prev,
          {
            id: newItemId(),
            file: newFile,
            thumbnail: URL.createObjectURL(newFile)
          }
        ]);
      },
      "image/png",
      1
    );

    cropper.current.destroy();
    setCropperReady(false);
    cropNextImage();
  }

  // Loads the next queued image into the cropper, or closes it when none remain.
  function cropNextImage() {
    const next = cropQueue.current.shift();
    croppingFile.current = next || null;
    props.setFile((prev) => ({
      ...prev,
      src: next ? URL.createObjectURL(next) : null
    }));
  }

  async function addPdfs(files) {
    const batch = [
      ...(props.pdfs || []).map((pdf) => pdf.file),
      ...pendingPdfs.current
    ];
    if (batch.length + files.length > MAX_UPLOAD_FILES) {
      setErrorStatus({
        code: "custom",
        message: `You can upload a maximum of ${MAX_UPLOAD_FILES} PDFs`
      });
      return;
    }

    const totalBytes = [...batch, ...files].reduce(
      (sum, file) => sum + file.size,
      0
    );
    if (totalBytes > MAX_PDF_TOTAL_BYTES) {
      setErrorStatus({
        code: "input",
        message: "Please upload files below 5MB in total"
      });
      return;
    }

    pendingPdfs.current = [...pendingPdfs.current, ...files];
    const results = await Promise.all(
      files.map((file) =>
        getPdfThumbnail(file)
          .then(({ thumbnail, pageCount }) => ({
            id: newItemId(),
            file,
            thumbnail,
            pageCount
          }))
          .catch(() => null)
      )
    );

    pendingPdfs.current = pendingPdfs.current.filter(
      (file) => !files.includes(file)
    );

    if (results.includes(null)) {
      setErrorStatus({
        code: "custom",
        message:
          "Some PDFs couldn't be read. They may be password-protected or damaged"
      });
    }

    const added = results.filter(Boolean);
    if (added.length) {
      props.setPdfs((prev) => [...prev, ...added]);
    }
  }

  function fileHandler(event) {
    const files = [...(event.target.files || [])];
    if (!files.length) return;

    const hasImageBatch = isValidArray(props.src);
    const hasPdfBatch =
      isValidArray(props.pdfs) || pendingPdfs.current.length > 0;

    if (files.every(isPdf) && !hasImageBatch) {
      addPdfs(files);
      return;
    }

    if (files.every(isImage) && !hasPdfBatch) {
      const imageCount = (props.src?.length || 0) + files.length;
      if (imageCount > MAX_UPLOAD_FILES) {
        setErrorStatus({
          code: "custom",
          message: `You can upload a maximum of ${MAX_UPLOAD_FILES} images`
        });
        return;
      }
      cropQueue.current = files;
      cropNextImage();
      return;
    }

    if (
      files.length === 1 &&
      files[0].type === "application/json" &&
      !hasImageBatch &&
      !hasPdfBatch
    ) {
      props.setFile((prev) => ({ ...prev, src: files[0] }));
      return;
    }

    const isMixed = files.every((file) => isImage(file) || isPdf(file));
    setErrorStatus({
      code: "custom",
      message: isMixed
        ? "Upload either images or PDFs, not both"
        : "Kindly upload a valid file"
    });
  }

  // skips the image being cropped; the rest of the queue still gets cropped
  function handleClose() {
    cropper.current?.destroy();
    setCropperReady(false);
    cropNextImage();
  }
  const handleClickDocumentUpload = (name) => {
    if (!name) {
      return;
    }

    const now = new Date();
    const payloadBase = {
      documentType: props.file.type,
      documentName: name,
      date: now.toISOString().split("T")[0],
      time: `${now.getHours()}:${now.getMinutes()}`
    };

    if (isValidArray(props.pdfs)) {
      uploadDocument({
        ...payloadBase,
        file: { src: props.pdfs.map((pdf) => pdf.file), type: "pdf" },
        fileConversion: true,
        navigate
      });
    } else if (isValidArray(props.src)) {
      // "<name>/<page>/<total>.png" tells the backend each page's position
      const files = props.src.map(
        ({ file }, index) =>
          new File([file], `${name}/${index + 1}/${props.src.length}.png`, {
            type: "image/png"
          })
      );
      uploadDocument({
        ...payloadBase,
        file: { src: files, type: "image" },
        fileConversion: true,
        navigate
      });
    } else if (props.file.src) {
      const extension = props.file.src.type.split("/").at(-1);
      const newFile = new File([props.file.src], `${name}.${extension}`, {
        type: props.file.src.type
      });

      uploadDocument({
        ...payloadBase,
        file: newFile,
        fileConversion:
          props.file.src?.type === "application/json" ? false : true,
        navigate
      });
    }
    props.setShow(false);
  };

  useEffect(() => {
    return () => {
      props.setShow(false);
      props.setFile({ type: "", src: "" });
      props.setSrc([]);
      props.setPdfs([]);
      cropQueue.current = [];
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!props.file || !props.file.type || !props.file.src) {
      return;
    }

    const imageTypes = [
      "prescription",
      "report",
      "admission",
      "insurance",
      "receipt",
      "vital",
      "vaccine",
      "consultation",
      "invoice",
      "others"
    ];

    const isImageType = imageTypes.includes(props.file.type);
    const isJson = props.file.src?.type === "application/json";

    if (isImageType && !isJson) {
      const canvas = document.getElementById("imageData");
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      const img = new Image();
      const maxW = 1024;
      const maxH = 1024;

      img.onload = () => {
        const { width: iw, height: ih } = img;
        const scale = Math.min(maxW / iw, maxH / ih);
        const iwScaled = iw * scale;
        const ihScaled = ih * scale;

        canvas.width = iwScaled;
        canvas.height = ihScaled;
        ctx.drawImage(img, 0, 0, iwScaled, ihScaled);

        cropper.current = new Cropper(canvas, {
          viewMode: 2,
          maxContainerWidth: 500,
          maxCanvasWidth: 100,
          maxCropBoxWidth: 20,
          background: false,
          borderRadius: 50,
          zoomable: true,
          dragMode: "move",
          responsive: true,
          guides: false,
          rotatable: true
        });

        setCropperReady(true);
      };

      img.src = props.file.src;
      return;
    }

    if (isJson && props.file.src.size) {
      if (props.file.src.size > 5_000_000) {
        setErrorStatus({
          code: "input",
          message: "Please upload a file below 5MB"
        });
      } else {
        props.setShow(true);
      }
    }
    // eslint-disable-next-line
  }, [props.file, cropper]);
  return (
    <>
      <input
        ref={props.documentUploadRef}
        type="file"
        id="prescription"
        name="fileUploaderInput"
        className="display-none"
        multiple
        accept={
          isValidArray(props.pdfs)
            ? "application/pdf"
            : isValidArray(props.src)
            ? "image/*"
            : "image/*,application/pdf,application/json"
        }
        onClick={(event) => {
          event.target.value = "";
        }}
        onChange={(event) => {
          fileHandler(event);
        }}
      />

      <DocumentUploadModal
        onClickDocumentUpload={(name) => {
          handleClickDocumentUpload(name);
        }}
        show={props.show}
        loading={props.loading}
        onClickClose={() => {
          props.setShow(false);
        }}
      />

      {/* An overlay, so the screen underneath (and the grid's batch) stays mounted */}
      {typeof props.file.src === "string" && props.file.src && (
        <div
          data-cy="CROPPER-MODAL"
          style={{ zIndex: 1000 }}
          className="position-fixed top-0 right-0 bottom-0 left-0 flex-center-children-horizontally flex-direction-column background-color-black"
        >
          <div className="inherit-parent-height inherit-parent-width max-height-90-percentage padding-top-default">
            <canvas
              className="display-block max-width-100-percentage"
              id="imageData"
              data-cy="CROPPER-CANVAS"
            />
          </div>
          <footer className="padding-larger inherit-parent-width flex-align-items-center flex-justify-content-space-between">
            <button
              data-cy="crop-img-cancel-button"
              type="submit"
              variant="secondary"
              className="background-transparent font-color-white font-family-gilroy-medium font-size-medium"
              onClick={() => {
                handleClose();
              }}
            >
              Cancel
            </button>
            <button
              data-cy="crop-img-rotate-button"
              type="button"
              className="background-transparent font-color-white  font-family-gilroy-medium font-size-medium"
              variant="secondary"
              onClick={() => {
                cropper.current.rotate(90);
                cropper.current.zoomTo(0);
              }}
            >
              Rotate
            </button>
            <button
              data-cy="CROP-IMG-SAVE-BUTTON"
              type="button"
              className="background-transparent font-color-white font-family-gilroy-medium font-size-medium"
              disabled={cropperReady === false}
              onClick={(event) => {
                if (
                  cropper.current.getData(true).width <= 50 ||
                  cropper.current.getData(true).height <= 50
                ) {
                  return;
                }
                cropHandler(event);
              }}
            >
              Save
            </button>
          </footer>
        </div>
      )}
    </>
  );
}

const DocumentUploadModal = (props) => {
  const [documentDetails, setDocumentDetails] = useState({
    name: "",
    date: ""
  });
  function documentUploadDetailsOnChange(event) {
    setDocumentDetails({
      ...documentDetails,
      [event.target.name]: event.target.value
    });
  }

  function validString(value) {
    return typeof value === "string" && value.trim().length !== 0;
  }

  useEffect(() => {
    return () => {
      setDocumentDetails({
        name: "",
        date: ""
      });
    };
  }, []);

  return (
    <Modal
      onClose={props.onClickClose}
      show={props.show}
      position="position-fixed bottom-0 left-0 right-0"
      boxShadow="none"
      borderRadius="none"
      width="inherit-parent-width"
      background="background-transparent"
      height="height-fit-to-content"
    >
      <form
        onChange={(event) => {
          documentUploadDetailsOnChange(event);
        }}
        onSubmit={(event) => {
          event.preventDefault();

          if (
            !validString(documentDetails.name)
            // || typeof documentDetails.date !== "string"
          )
            return;
          const timestamp = +new Date(documentDetails.date);
          props.onClickDocumentUpload(documentDetails.name, timestamp);
        }}
        className="background-white padding-left-larger padding-right-larger font-family-gilroy-medium user-select-none "
      >
        <section className="max-width-588px margin-horizontal-auto">
          <div className="font-color-secondary font-family-gilroy-regular font-size-small text-align-center padding-top-large padding-bottom-medium">
            ADD DOCUMENT
          </div>
          <InputBox
            label="Document name"
            name="name"
            type="text"
            data-cy="DOCUMENT-NAME"
            className="padding-right-medium margin-bottom-large"
            value={documentDetails.name}
            autoFocus={true}
          />
          {/* <InputBox
            label="Date of issue"
            name="date"
            type="date"
            data-cy="documentIssuedDate"
            className="padding-right-medium margin-bottom-larger"
            value={documentDetails.date}
            autoFocus={false}
          /> */}
          <div className="display-flex padding-bottom-larger">
            <Button
              buttonKey="DOCUMENT-NAME-ADD-BUTTON"
              data-cy="DOCUMENT-NAME-ADD-BUTTON"
              boxShadow={false}
              className="margin-right-small font-family-gilroy-medium"
              text="Add"
              loading={props.loading}
              disabled={
                validString(documentDetails.name)
                  ? // &&  typeof documentDetails.date === "string"
                    false
                  : true
              }
              type="submit"
            />
            <Button
              buttonKey={"document-name-cancel-button"}
              boxShadow={false}
              className="margin-left-small font-family-gilroy-regular font-color-secondary border-1px-e5e5e5"
              variant="secondary"
              text="Cancel"
              onClick={props.onClickClose}
            />
          </div>
        </section>
      </form>
    </Modal>
  );
};
